import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Check, Loader2 } from "lucide-react";
import { FadeIn, MaskText, SectionTag } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initialForm = { name: "", email: "", company: "", message: "" };

const ContactSection = () => {
  const [form, setForm] = useState(initialForm);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/contact`, {
        name: form.name.trim(),
        email: form.email.trim(),
        company: form.company.trim() || null,
        message: form.message.trim(),
      });
      setSent(true);
      setForm(initialForm);
      toast.success("Message received. We'll be in touch.");
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSending(false);
    }
  };

  const inputCls =
    "w-full border-b border-white/20 bg-transparent py-4 font-body text-base text-white placeholder:text-ash/50 transition-[border-color] duration-300 focus:border-ember focus:outline-none";

  return (
    <section id="contact" data-testid="contact-section" className="relative border-t border-white/10 py-28 md:py-40">
      <div className="mx-auto grid max-w-[1600px] grid-cols-1 gap-20 px-6 md:px-12 lg:grid-cols-12">
        <div className="lg:col-span-5">
          <SectionTag index="03" label="Contact" />
          <h2 data-testid="contact-heading" className="mt-8 font-display font-semibold uppercase leading-[0.9] tracking-tight">
            <MaskText
              lines={[
                <span key="1" className="text-[clamp(3rem,7vw,7rem)] text-white">Talk To</span>,
                <span key="2" className="text-[clamp(3rem,7vw,7rem)] text-stroke-silver">The Shop.</span>,
              ]}
            />
          </h2>
          <FadeIn delay={0.25}>
            <p className="mt-8 max-w-md font-body text-base font-light leading-relaxed text-ash">
              Questions, early interest in PrePipe Station, or a jobsite problem you think
              we should solve next — send a message. Every enquiry is read.
            </p>
          </FadeIn>
          <FadeIn delay={0.35} className="mt-14">
            <img src="/logo.png" alt="Proxima Centauri Tools logo" className="w-36 mix-blend-screen opacity-90" />
          </FadeIn>
        </div>

        <FadeIn className="lg:col-span-7" delay={0.15}>
          {sent ? (
            <div
              data-testid="contact-success"
              className="flex h-full min-h-[420px] flex-col items-start justify-center border border-ember/40 bg-carbon p-12"
            >
              <span className="flex h-14 w-14 items-center justify-center border border-ember red-glow-box">
                <Check size={26} className="text-ember" />
              </span>
              <h3 className="mt-8 font-display text-5xl font-semibold uppercase leading-none text-white">
                Message Received.
              </h3>
              <p className="mt-5 max-w-md font-body text-base font-light leading-relaxed text-ash">
                Thanks for reaching out to Proxima Centauri Tools. We'll get back to you as soon as possible.
              </p>
              <button
                data-testid="contact-send-another-button"
                onClick={() => setSent(false)}
                className="mt-10 border border-steel/30 px-7 py-3 font-display text-lg font-semibold uppercase tracking-[0.15em] text-steel transition-[border-color,color] duration-300 hover:border-ember hover:text-white"
              >
                Send Another
              </button>
            </div>
          ) : (
            <form data-testid="contact-form" onSubmit={onSubmit} className="flex flex-col gap-10 lg:pt-24">
              <div className="grid grid-cols-1 gap-10 md:grid-cols-2">
                <label className="block">
                  <span className="font-body text-[11px] font-medium uppercase tracking-[0.3em] text-ash">Name *</span>
                  <input
                    data-testid="contact-name-input"
                    required
                    value={form.name}
                    onChange={set("name")}
                    placeholder="Your name"
                    className={inputCls}
                  />
                </label>
                <label className="block">
                  <span className="font-body text-[11px] font-medium uppercase tracking-[0.3em] text-ash">Email *</span>
                  <input
                    data-testid="contact-email-input"
                    required
                    type="email"
                    value={form.email}
                    onChange={set("email")}
                    placeholder="you@company.com"
                    className={inputCls}
                  />
                </label>
              </div>
              <label className="block">
                <span className="font-body text-[11px] font-medium uppercase tracking-[0.3em] text-ash">Company</span>
                <input
                  data-testid="contact-company-input"
                  value={form.company}
                  onChange={set("company")}
                  placeholder="Optional"
                  className={inputCls}
                />
              </label>
              <label className="block">
                <span className="font-body text-[11px] font-medium uppercase tracking-[0.3em] text-ash">Message *</span>
                <textarea
                  data-testid="contact-message-input"
                  required
                  rows={4}
                  value={form.message}
                  onChange={set("message")}
                  placeholder="Tell us what's happening on your jobsite."
                  className={`${inputCls} resize-none`}
                />
              </label>
              <div>
                <button
                  data-testid="contact-submit-button"
                  type="submit"
                  disabled={sending}
                  className="group relative overflow-hidden bg-ember px-10 py-4 font-display text-xl font-semibold uppercase tracking-[0.15em] text-white transition-[box-shadow,transform,opacity] duration-300 hover:-translate-y-0.5 hover:shadow-[0_0_44px_-6px_rgba(230,0,0,0.65)] disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <span className="relative z-10 flex items-center gap-3">
                    {sending && <Loader2 size={18} className="animate-spin" />}
                    {sending ? "Sending" : "Send Message"}
                  </span>
                  <span className="absolute inset-0 -translate-x-full bg-white/20 transition-transform duration-500 group-hover:translate-x-0" />
                </button>
              </div>
            </form>
          )}
        </FadeIn>
      </div>
    </section>
  );
};

export default ContactSection;
